import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import AnnouncementBar from './components/AnnouncementBar';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
import { initAnalytics, trackPageview } from './lib/consent';
import { BookingProvider } from './context/BookingContext';
import { useDonorSession } from './lib/useDonorSession';

import Landing from './pages/Landing';
import Explore from './pages/Explore';
import MonasteryDetail from './pages/MonasteryDetail';
import MonasteryAbout from './pages/MonasteryAbout';
import ClaimMonastery from './pages/ClaimMonastery';
import Offer from './pages/Offer';
import Checkout from './pages/Checkout';
import CheckoutPay from './pages/CheckoutPay';
import Confirmation from './pages/Confirmation';
import Offerings from './pages/Offerings';
import Saved from './pages/Saved';
import About from './pages/About';
import Contact from './pages/Contact';
import Credits from './pages/Credits';
import Legal from './pages/Legal';
import NotFound from './pages/NotFound';

/**
 * The portals live behind a single lazy boundary — see portal/PortalRoutes.
 */
const PortalRoutes = lazy(() => import('./portal/PortalRoutes'));

/**
 * The donor account page is lazy for the same reason the portals are.
 */
const AccountPage = lazy(() => import('./account/AccountPage'));
const CompleteProfile = lazy(() => import('./account/CompleteProfile'));

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

/**
 * Placing an offering requires a signed-in donor with a completed profile —
 * see `requireAuth` and `requireCompletedProfile` on `POST /api/orders`
 * server-side, which is the actual enforcement. This is just so a visitor is
 * walked through sign-in and registration before building a doomed checkout,
 * not left to hit a 401/403 at the end of it.
 */
function RequireDonor({ children }) {
  const { user, loading } = useDonorSession();
  const location = useLocation();
  if (loading) {
    return <div className="shell section"><div className="skeleton" style={{ height: 200 }} /></div>;
  }
  if (!user) {
    return <Navigate to={`/account?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (!user.profileComplete) {
    return <Navigate to={`/account/complete?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return children;
}

/**
 * Loads Analytics (once — `initAnalytics` no-ops after the first call) and
 * sends one virtual pageview per route change. `gtag('config', ...)` only
 * fires the first pageview on its own; a client-routed app that never
 * reloads has to send the rest itself, or every page after the first is
 * invisible to Analytics.
 */
function AnalyticsPageview() {
  const { pathname } = useLocation();
  useEffect(() => {
    initAnalytics();
    trackPageview(pathname);
  }, [pathname]);
  return null;
}

export default function App() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  if (/^\/(partner|admin|portal)(\/|$)/.test(pathname)) {
    return (
      <Suspense fallback={<div className="portal-waiting" />}>
        <PortalRoutes />
      </Suspense>
    );
  }

  return (
    <BookingProvider>
      <div className="page">
        <a className="skip-link" href="#main">{t('common.skipToContent', 'Skip to content')}</a>
        <ScrollToTop />
        <AnalyticsPageview />
        <AnnouncementBar />
        <SiteHeader />

        <main className="page-body" id="main" tabIndex={-1}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/monastery/:id" element={<MonasteryDetail />} />
            <Route path="/monastery/:id/about" element={<MonasteryAbout />} />
            <Route path="/monastery/:id/claim" element={<ClaimMonastery />} />
            <Route path="/monastery/:id/offer/:serviceId" element={<Offer />} />
            <Route path="/checkout" element={<RequireDonor><Checkout /></RequireDonor>} />
            <Route path="/checkout/pay/:reference" element={<RequireDonor><CheckoutPay /></RequireDonor>} />
            <Route path="/confirmation/:referenceId" element={<Confirmation />} />
            <Route path="/offerings" element={<Offerings />} />
            <Route path="/saved" element={<Saved />} />
            <Route
              path="/account"
              element={
                <Suspense fallback={<div className="shell section" />}>
                  <AccountPage />
                </Suspense>
              }
            />
            <Route
              path="/account/complete"
              element={
                <Suspense fallback={<div className="shell section" />}>
                  <CompleteProfile />
                </Suspense>
              }
            />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/credits" element={<Credits />} />
            <Route path="/terms" element={<Legal slug="terms" />} />
            <Route path="/privacy" element={<Legal slug="privacy" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        <SiteFooter />
      </div>
    </BookingProvider>
  );
}
